// Structure and Tailwind classNames ported from the Lovable reference
// implementation (files/snapdraw-design-kit/code/src/routes/reset-password.tsx),
// built on the shared AuthLayout. Retrofitted with the real logic from the
// prior plain-CSS version: confirm the recovery session Supabase establishes
// from the emailed link actually exists before showing the form, call
// supabase.auth.updateUser to set the new password, and redirect to the
// dashboard on success. The "expires in" copy is corrected to 60 minutes to
// match this project's actual Supabase reset-link expiry (see ForgotPasswordPage).
// The generic submit error (real updateUser failure) now uses the shared
// FieldError component for consistency with the other auth screens
// (2026-08-22) — this page's own password-rule checklist already covers
// field-level validation, so no separate per-field errors are needed here.
// The error stores an i18n-aware Message (not a resolved string) so it
// re-resolves via t() at render time and doesn't freeze in whatever
// language was active when it was set (2026-08-22 fix). The real Supabase
// failure case is genuinely untranslatable third-party text (see Message's
// `raw` case in @/lib/i18n/message).
// Both password fields have a show/hide toggle (2026-08-22) via the shared
// PasswordInput component.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ShieldCheck } from "lucide-react";

import { authPrimaryClass, AuthLayout } from "@/components/snapdraw/auth-layout";
import { FieldError } from "@/components/snapdraw/form";
import { PasswordInput } from "@/components/snapdraw/password-input";
import { supabase } from "@/lib/supabase";
import { useT } from "@/lib/i18n";
import { resolveMessage, type Message } from "@/lib/i18n/message";
import "@/styles/tailwind-preview.css";

const RULES = [
  { key: "reset.rule.length", test: (v: string) => v.length >= 8 },
  { key: "reset.rule.number", test: (v: string) => /\d/.test(v) },
  { key: "reset.rule.letter", test: (v: string) => /[a-zA-Z]/.test(v) },
];

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const t = useT();
  const [ready, setReady] = useState(false);
  const [validLink, setValidLink] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<Message | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // The recovery link establishes a session; confirm we actually have one so
    // we don't show the form to someone who navigated here directly.
    supabase.auth.getSession().then(({ data }) => {
      setValidLink(Boolean(data.session));
      setReady(true);
    });
  }, []);

  const passed = RULES.filter((r) => r.test(password)).length;
  const matches = password.length > 0 && password === confirm;
  const isReady = passed === RULES.length && matches;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isReady) return;
    setError(null);
    setBusy(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      setDone(true);
      setTimeout(() => navigate("/", { replace: true }), 1200);
    } catch (err) {
      setError(err instanceof Error ? { raw: err.message } : { key: "reset.error.generic" });
    } finally {
      setBusy(false);
    }
  }

  if (!ready) return <div className="iv-loading">{t("state.loading")}</div>;

  return (
    <AuthLayout
      eyebrow={t("reset.eyebrow")}
      footer={
        !validLink && (
          <p className="mt-6 text-center text-sm text-foreground/80">
            {t("reset.linkExpired")}{" "}
            <Link to="/forgot-password" className="text-arc hover:text-spark">
              {t("reset.sendNew")}
            </Link>
          </p>
        )
      }
    >
      {!validLink ? (
        <div className="mt-6 rounded-xl border border-destructive/40 bg-destructive/10 p-6">
          <p className="font-display text-2xl uppercase tracking-wide text-destructive">
            {t("reset.invalidTitle")}
          </p>
          <p className="mt-3 text-sm text-foreground/80">{t("reset.invalidBody")}</p>
        </div>
      ) : done ? (
        <div className="mt-6 rounded-xl border border-arc/50 bg-black/30 p-6">
          <p className="font-display text-2xl uppercase tracking-wide text-arc">{t("reset.doneTitle")}</p>
          <p className="mt-3 text-sm text-foreground/80">{t("reset.doneBody")}</p>
        </div>
      ) : (
        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          {error && <FieldError>{resolveMessage(t, error)}</FieldError>}

          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("reset.newPasswordPlaceholder")}
            aria-label={t("reset.newPasswordPlaceholder")}
            autoComplete="new-password"
          />

          <PasswordInput
            icon={ShieldCheck}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={t("reset.confirmPasswordPlaceholder")}
            aria-label={t("reset.confirmPasswordPlaceholder")}
            autoComplete="new-password"
          />

          <div className="rounded-xl border border-border bg-black/30 p-4">
            <p className="sd-label">{t("reset.strength")}</p>
            <div className="mt-3 flex gap-1.5" aria-hidden="true">
              {RULES.map((r, i) => (
                <span
                  key={r.key}
                  className={`h-1.5 flex-1 rounded-full ${i < passed ? "bg-arc" : "bg-white/10"}`}
                />
              ))}
            </div>
            <ul className="mt-4 space-y-2">
              {RULES.map((r) => {
                const ok = r.test(password);
                return (
                  <li key={r.key} className="flex items-center gap-2 text-sm">
                    <span
                      className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                        ok ? "border-arc text-arc" : "border-border text-transparent"
                      }`}
                    >
                      <Check className="h-3 w-3" />
                    </span>
                    <span className={ok ? "text-foreground" : "text-muted-foreground"}>{t(r.key)}</span>
                  </li>
                );
              })}
              <li className="flex items-center gap-2 text-sm">
                <span
                  className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                    matches ? "border-arc text-arc" : "border-border text-transparent"
                  }`}
                >
                  <Check className="h-3 w-3" />
                </span>
                <span className={matches ? "text-foreground" : "text-muted-foreground"}>{t("reset.rule.match")}</span>
              </li>
            </ul>
          </div>

          <button
            type="submit"
            className={`${authPrimaryClass} disabled:cursor-not-allowed disabled:opacity-45`}
            disabled={!isReady || busy}
          >
            {busy ? t("reset.updating") : t("reset.submit")}
          </button>

          <p className="text-center text-sm text-muted-foreground">{t("reset.footer")}</p>
        </form>
      )}
    </AuthLayout>
  );
}
